import { useMemo, useEffect } from 'react';
import { useRouter } from 'next/router';

import { useMatchMedia } from '../../hooks';

import { Link, Markdown, Svg } from '../../snippets';

export function ReferralContent({ aboveTheFold, content }) {
  const {
    alignmentDesktop,
    alignmentMobile,
    buttons,
    color,
    heading,
    headingStyle,
    maxWidthDesktop,
    superheading,
    subtext,
    html,
    svg,
  } = { ...content };
  const isMobileViewport = useMatchMedia('(max-width: 1023px)');
  const router = useRouter();

  const alignmentClasses = `${alignmentMobile} ${alignmentDesktop}`;

  const headingWithBreaks = useMemo(() => {
    const splitHeading = heading?.split('\n');
    if (splitHeading?.length === 1) return heading;
    return splitHeading?.reduce((acc, line, index, arr) => {
      acc.push(<span key={index}>{line}</span>);
      if (index < arr.length - 1) acc.push(<br key={`br${index}`} />);
      return acc;
    }, []);
  }, [heading]);

  const svgWidth = isMobileViewport ? svg?.svgMobileWidth : svg?.svgDesktopWidth;
  const svgHeight = isMobileViewport ? svg?.svgMobileHeight : svg?.svgDesktopHeight;

  const loadOkendoScript = () => {
    // Remove existing script if it exists
    const existingScript = document.querySelector('script[src="https://d3hw6dc1ow8pp2.cloudfront.net/referrals/js/referrals-api.js"]');
    if (existingScript) {
      existingScript.remove();
    }

    // Dynamically add the Okendo script
    const script = document.createElement('script');
    script.src = 'https://d3hw6dc1ow8pp2.cloudfront.net/referrals/js/referrals-api.js';
    script.type = 'text/javascript';
    script.defer = true;
    script.onload = () => {
      if (window.okeReferrals) {
        window.okeReferrals.init(); // Initialize the Okendo widget
      }
    };
    document.head.appendChild(script);
  };

  useEffect(() => {
    // Load the Okendo script when the component mounts
    loadOkendoScript();

    // Reload the script on route changes
    const handleRouteChange = () => {
      loadOkendoScript();
    };

    router.events.on('routeChangeComplete', handleRouteChange);

    // Cleanup listener on component unmount
    return () => {
      router.events.off('routeChangeComplete', handleRouteChange);
    };
  }, [router.events]);

  return (
    <>
      {/* Add Okendo Referral Settings */}
      <script
        id="okeReferralSettings"
        type="application/json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            subscriberId: '2ff2e00e-dfb3-49a9-adab-140f21da02c7', // Replace with your actual subscriberId
          }),
        }}
      />
      <div className="px-contained py-contained w-full">
        <div
          className={`mx-auto flex flex-col gap-5 ${alignmentClasses} ${maxWidthDesktop}`}
          style={{ color }}
        >
          <div>
            {superheading && (
              <p className="text-superheading mb-1 lg:mb-1.5">{superheading}</p>
            )}

            {aboveTheFold ? (
              <h1 className={`${headingStyle == 'big' ? 'text-5xl md:text-8xl uppercase' : 'text-title-h1' } ${svg?.svgName && 'sr-only'}`}>{headingWithBreaks}</h1>
            ) : (
              <h2 className={`${headingStyle == 'big' ? 'text-5xl md:text-8xl uppercase' : 'text-title-h2' } ${svg?.svgName && 'sr-only'}`}>{headingWithBreaks}</h2>
            )}
            
            {svg?.svgName && (
              <Svg
                src={`/svgs/noprecache/content/${svg?.svgName}.svg#${svg?.svgName}`}
                alt={`${svg?.svgAlt} Icon`}
                title={`${svg?.svgAlt}`}
                style={{ width: `${svgWidth}px`, height: `${svgHeight}px` }}
              />
            )}

            {html && (
              <div
                className="mx-auto flex flex-col [&>:first-child]:mt-0 [&>:last-child]:mb-0 [&_a]:underline [&_h1]:mb-6 [&_h2]:mt-8 [&_h2]:mb-5 [&_h3]:mb-4 [&_h3]:mt-6 [&_h4]:mb-4 [&_h4]:mt-4 [&_h5]:mb-4 [&_h5]:mt-2 [&_h6]:mb-4 [&_li>p]:mb-0 [&_li]:mb-2 [&_ol>li]:list-decimal [&_ol]:mb-4 [&_ol]:pl-8 [&_p]:mb-4 [&_ul>li]:list-disc [&_ul]:mb-4 [&_ul]:pl-8"
                // eslint-disable-next-line react/no-danger
                dangerouslySetInnerHTML={{ __html: html }}
              />
            )}
            
          </div>

          {subtext && (
            <div className="[&_a]:underline [&_h1]:text-base [&_h2]:text-base [&_h3]:text-base [&_h4]:text-base [&_h5]:text-base [&_h6]:text-base [&_p]:text-base">
              <Markdown>{subtext}</Markdown>
            </div>
          )}

          {buttons?.length > 0 && (
            <ul className="flex flex-col justify-center gap-4 xs:flex-row">
              {buttons.slice(0, 2).map(({ link, style }, index) => {
                return (
                  <li key={index}>
                    <Link
                      aria-label={
                        link?.newTab 
                          ? `Open in a new tab to view ${link?.text}` 
                          : link?.text
                      }
                      className={style}
                      href={link?.url}
                      newTab={link?.newTab}
                      type={link?.type}
                    >
                      {link?.text}
                    </Link>
                  </li>
                );
              })}
            </ul>
          )}

          <div data-oke-referrals-widget/>
        </div>
      </div>
    </>
  );
}

ReferralContent.displayName = 'ReferralContent';